'use client';

import { useRef, useEffect } from 'react';
import { useTheme } from '@/components/marketing/engine/theme-context';

/* ───────────────── Signal Singularity ─────────────────
   Signal fragments orbit & spiral inward toward a luminous core.
   Verified terms drift and revolve through the field. */

const FRAGMENTS = [
  'score 91', 'gaze ✓', 'identity', 'proctored', 'tab focus', 'lat 182ms',
  'resume', 'reference', 'audio ✓', 'employment', 'sig verify', 'face 99.4%',
  'transcript', 'assessment', 'depth: adv', 'integrity', 'consent', 'verified',
];

interface Particle {
  ang: number;
  rad: number;
  baseRad: number;
  angV: number;
  radV: number;
  size: number;
  bright: number;
  frag: string | null;
  jx: number;
  jy: number;
}

interface SingularityProps {
  density?: number;
}

export function Singularity({ density = 1 }: SingularityProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let w = 0, h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let cx = 0, cy = 0, raf: number;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const palette = () => themeRef.current === 'light'
      ? { core: '99,102,241', ring: '79,70,229', frag: '79,70,229', mark: '22,133,95', trail: '236,238,247' }
      : { core: '129,140,248', ring: '99,102,241', frag: '129,140,248', mark: '52,211,156', trail: '6,6,15' };

    function resize() {
      const r = canvas!.getBoundingClientRect();
      w = r.width; h = r.height;
      canvas!.width = w * dpr; canvas!.height = h * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = w / 2; cy = h / 2;
    }
    resize();
    const ro = new ResizeObserver(resize); ro.observe(canvas);

    const maxR = () => Math.max(Math.min(w, h) * 0.52, 60);
    const N = Math.round(Math.min(90, Math.max(54, (w * h) / 9000)) * density);
    let frame = 0, lastGlow = -1;
    const particles: Particle[] = [];
    for (let i = 0; i < N; i++) {
      const r = Math.random() * maxR() + 30;
      particles.push({
        ang: Math.random() * Math.PI * 2,
        rad: r, baseRad: r,
        angV: (0.00040 + Math.random() * 0.00100),
        radV: 0.009 + Math.random() * 0.022,
        size: 0.6 + Math.random() * 1.8,
        bright: 0.3 + Math.random() * 0.7,
        frag: Math.random() < 0.22 ? FRAGMENTS[(Math.random() * FRAGMENTS.length) | 0] : null,
        jx: (Math.random() - 0.5) * 1.7, jy: (Math.random() - 0.5) * 1.7,
      });
    }

    let last = performance.now();
    let visible = true; // on-screen AND tab not hidden

    function step(now: number) {
      raf = requestAnimationFrame(step); // always keep the loop alive (browser throttles hidden tabs)
      if (!visible) { last = now; return; } // scrolled away: skip all drawing work
      const dt = Math.min(now - last, 50); last = now;
      void dt; // dt available for future use

      const p = palette();
      const mR = maxR();
      const lightMode = themeRef.current === 'light';
      // additive glow reads beautifully on the near-black dark theme, but blows out to white on the
      // light theme — so composite normally in light mode (indigo stays indigo on the pale bg).
      const blend: GlobalCompositeOperation = lightMode ? 'source-over' : 'lighter';

      // Trail fade — solid fill EXACTLY matching the hero's solid bg, so the canvas leaves
      // no visible ellipse/seam; only the luminous particles read on top. Light mode fades
      // slower (lower alpha) so the inward spiral leaves a fainter persistent trace — the
      // field reads as indigo INK drawn on paper rather than light blooming in space.
      ctx!.globalCompositeOperation = 'source-over';
      ctx!.fillStyle = lightMode ? `rgba(${p.trail},0.22)` : `rgba(${p.trail},0.32)`;
      ctx!.fillRect(0, 0, w, h);
      ctx!.globalCompositeOperation = blend;

      // Core — dark mode: a luminous bloom (additive). Light mode: a dense, saturated
      // ink pool concentrating to a defined center with a crisp falloff, so it holds its
      // own on the pale surface instead of washing out.
      const corePulse = 0.5 + 0.5 * Math.sin(now / 3400);
      const coreR = (Math.min(w, h) * 0.07) * (1 + corePulse * 0.12);
      const g = ctx!.createRadialGradient(cx, cy, 0, cx, cy, coreR * 4.5);
      if (lightMode) {
        g.addColorStop(0, `rgba(${p.ring},0.42)`);
        g.addColorStop(0.14, `rgba(${p.ring},0.22)`);
        g.addColorStop(0.4, `rgba(${p.core},0.07)`);
        g.addColorStop(1, `rgba(${p.ring},0)`);
      } else {
        g.addColorStop(0, `rgba(${p.core},0.6)`);
        g.addColorStop(0.18, `rgba(${p.ring},0.26)`);
        g.addColorStop(0.5, `rgba(${p.ring},0.06)`);
        g.addColorStop(1, `rgba(${p.ring},0)`);
      }
      ctx!.fillStyle = g;
      ctx!.beginPath(); ctx!.arc(cx, cy, coreR * 4.5, 0, Math.PI * 2); ctx!.fill();

      // expose intensity to DOM (throttled hard — :root custom-prop writes trigger style recalc, keep them rare)
      frame++;
      if (frame % 24 === 0) {
        const glow = +(0.5 + corePulse * 0.22).toFixed(2);
        if (glow !== lastGlow) { document.documentElement.style.setProperty('--sing-glow', String(glow)); lastGlow = glow; }
      }

      // Particles — the signal field. Verified terms drift and revolve through it.
      for (let i = 0; i < particles.length; i++) {
        const pt = particles[i];
        if (!reduced) {
          pt.ang += pt.angV * (1 + (mR / Math.max(pt.rad, 40)) * 0.28);
          pt.rad -= pt.radV * (0.5 + (mR - pt.rad) / mR);
          if (pt.rad < coreR * 0.7) { pt.rad = mR + Math.random() * 40; pt.bright = 0.3 + Math.random() * 0.7; }
        }
        const x = cx + Math.cos(pt.ang) * pt.rad;
        const y = cy + Math.sin(pt.ang) * pt.rad;
        // Light mode lifts particle opacity — without additive blending the dots need more
        // alpha to register as crisp indigo marks on the pale surface.
        const a = (0.18 + pt.bright * 0.55 * (1 - pt.rad / (mR * 1.4))) * (lightMode ? 1.3 : 1);
        ctx!.beginPath();
        ctx!.fillStyle = `rgba(${p.frag},${Math.max(0, a)})`;
        ctx!.arc(x, y, pt.size, 0, Math.PI * 2);
        ctx!.fill();

        // revolving fragment term — clearly legible, fading only at the very inner/outer edges of the field
        if (pt.frag && pt.rad > mR * 0.40 && pt.rad < mR * 1.06) {
          const band = Math.min(1, (pt.rad - mR * 0.40) / (mR * 0.14)) * Math.min(1, (mR * 1.06 - pt.rad) / (mR * 0.24));
          ctx!.globalCompositeOperation = 'source-over';
          ctx!.font = '10px "Geist Mono", monospace';
          ctx!.fillStyle = lightMode
            ? `rgba(${p.frag},${0.30 + band * 0.42})`
            : `rgba(${p.frag},${0.20 + band * 0.32})`;
          ctx!.fillText(pt.frag, x + 7, y - 4);
          ctx!.globalCompositeOperation = blend;
        }
      }
    }
    raf = requestAnimationFrame(step);

    let onScreen = true;
    const updateVisible = () => { visible = onScreen && !document.hidden; if (visible) last = performance.now(); };
    const onVis = () => updateVisible();
    document.addEventListener('visibilitychange', onVis);
    // Skip drawing work when the field is scrolled off-screen — no wasted frames, no resume race.
    const io = new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; updateVisible(); }, { threshold: 0 });
    io.observe(canvas);

    return () => { cancelAnimationFrame(raf); ro.disconnect(); io.disconnect(); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />;
}
